/**
 * /trip/:id/itinerary  — Public shareable itinerary page
 * No login required. Shows full route with stops, dates, capacity.
 * Has WhatsApp / copy link share buttons.
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { useLang } from '../hooks/useLang';

export default function TripItinerary() {
  const { id }    = useParams();
  const { t }     = useLang();
  const [trip,   setTrip]   = useState(null);
  const [stops,  setStops]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied,  setCopied]  = useState(false);

  useEffect(() => {
    async function load() {
      const { data: tripData } = await supabase
        .from('trips')
        .select('*, profiles!transporter_id(full_name, phone, rating, total_reviews, is_verified)')
        .eq('id', id)
        .single();

      if (!tripData) { setLoading(false); return; }
      setTrip(tripData);

      if (tripData.is_multi_stop) {
        const { data: stopsData } = await supabase
          .from('trip_stops')
          .select('*')
          .eq('trip_id', id)
          .order('sequence', { ascending: true });
        setStops(stopsData || []);
      }
      setLoading(false);
    }
    load();
  }, [id]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div className="spinner" />
    </div>
  );

  if (!trip) return (
    <div className="page"><div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
      <h2>{t('itinerary.tripNotFound')}</h2>
      <Link to="/search" className="btn btn-primary" style={{ marginTop: '20px' }}>Browse trips</Link>
    </div></div>
  );

  const transporter = trip.profiles || {};
  const pageUrl     = typeof window !== 'undefined' ? window.location.href : `https://transportily.app/trip/${id}/itinerary`;
  const pickupCities   = trip.is_multi_stop
    ? stops.filter(s => s.stop_type === 'pickup' || s.stop_type === 'both').map(s => s.city).join(', ')
    : trip.origin_city;
  const deliveryCities = trip.is_multi_stop
    ? stops.filter(s => s.stop_type === 'delivery' || s.stop_type === 'both').map(s => s.city).join(', ')
    : trip.destination_city;

  const whatsappText = encodeURIComponent(
    `🚚 *Transportily — ${trip.origin_city} → ${trip.destination_city}*\n` +
    (trip.is_multi_stop ? `📍 Pickup stops: ${pickupCities}\n🏁 Delivery stops: ${deliveryCities}\n` : '') +
    `📅 Departing: ${trip.departure_date ? format(new Date(trip.departure_date), 'dd MMM yyyy') : '—'}\n` +
    `⚖️ ${trip.available_kg} ${t('itinerary.available')} · €${trip.price_per_kg}/kg\n` +
    `👤 ${transporter.full_name || 'Transporter'}\n\n` +
    `Book your spot 👇\n${pageUrl}`
  );

  function copyLink() {
    navigator.clipboard.writeText(pageUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const stopTypeStyle = (type) => ({
    pickup:   { bg: 'rgba(232,52,26,0.15)',   border: 'rgba(232,52,26,0.35)',   label: t('itinerary.stopPickup'),   dot: '#E8341A' },
    delivery: { bg: 'rgba(76,175,125,0.15)',  border: 'rgba(76,175,125,0.35)', label: t('itinerary.stopDelivery'), dot: '#4CAF7D' },
    port:     { bg: 'rgba(100,150,255,0.15)', border: 'rgba(100,150,255,0.35)', label: t('itinerary.stopPort'),    dot: '#6496ff' },
    both:     { bg: 'rgba(232,197,71,0.15)',  border: 'rgba(232,197,71,0.35)', label: t('itinerary.stopBoth'),     dot: '#E8C547' },
  })[type] || { bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.1)', label: type, dot: '#fff' };

  return (
    <div className="page" style={{ overflowX: 'hidden' }}>
      <div className="container" style={{ maxWidth: '680px', paddingTop: '20px', paddingBottom: '60px' }}>

        {/* Back */}
        <Link to="/search" className="back-link">{t('itinerary.backToSearch')}</Link>

        {/* ── HEADER ─────────────────────────────────────────── */}
        <div style={{ marginBottom: '28px' }}>
          {/* Multi-stop badge */}
          {trip.is_multi_stop && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(232,52,26,0.1)', border: '1px solid rgba(232,52,26,0.3)', borderRadius: '100px', padding: '4px 12px', fontSize: '12px', fontWeight: '700', color: 'var(--red)', marginBottom: '14px' }}>
              {t('itinerary.multiStopBadge')}
            </div>
          )}

          <h1 style={{ fontFamily: 'Syne', fontWeight: '900', fontSize: 'clamp(24px, 6vw, 40px)', letterSpacing: '-1.5px', lineHeight: '1.05', marginBottom: '12px' }}>
            {trip.origin_city}
            <span style={{ color: 'var(--red)', margin: '0 10px' }}>→</span>
            {trip.destination_city}
          </h1>

          {/* Transporter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', marginBottom: '16px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '800', color: '#fff', flexShrink: 0 }}>
              {transporter.full_name?.[0] || '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '700', fontSize: '15px' }}>{transporter.full_name || '—'}</div>
              <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>
                {transporter.is_verified && <span style={{ color: '#4CAF7D', marginRight: '8px' }}>✓ Verified</span>}
                {transporter.rating > 0 && <span>★ {transporter.rating?.toFixed(1)} ({transporter.total_reviews} reviews)</span>}
              </div>
            </div>
            {transporter.phone && (
              <a href={`https://wa.me/${transporter.phone.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer"
                style={{ background: '#25D366', color: '#fff', padding: '7px 12px', borderRadius: '100px', fontSize: '13px', fontWeight: '600', textDecoration: 'none', flexShrink: 0 }}>
                💬 WhatsApp
              </a>
            )}
          </div>

          {/* Key stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
            {[
              { label: 'Available', value: `${trip.available_kg} kg` },
              { label: 'Price', value: `€${trip.price_per_kg}/kg` },
              { label: 'Departs', value: trip.departure_date ? format(new Date(trip.departure_date), 'dd MMM yyyy') : '—' },
            ].map((s, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'Syne', fontWeight: '800', fontSize: '16px', marginBottom: '3px' }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: 'var(--olive)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Accept badges */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            {trip.accepts_fragile && <span className="tag">🫧 Fragile OK</span>}
            {trip.accepts_bulky   && <span className="tag">📦 Bulky OK</span>}
            {trip.home_pickup     && <span className="tag">🏠 Home pickup</span>}
            {trip.offers_home_delivery && <span className="tag">🚚 Home delivery</span>}
          </div>
        </div>

        {/* ── ITINERARY (multi-stop) ─────────────────────────── */}
        {trip.is_multi_stop && stops.length > 0 && (
          <div style={{ marginBottom: '28px' }}>
            <h2 style={{ fontFamily: 'Syne', fontWeight: '800', fontSize: '18px', marginBottom: '16px', letterSpacing: '-0.5px' }}>
              📍 {t('itinerary.fullItinerary')}
            </h2>

            <div style={{ position: 'relative' }}>
              {/* Vertical timeline line */}
              <div style={{ position: 'absolute', left: '19px', top: '20px', bottom: '20px', width: '2px', background: 'rgba(255,255,255,0.07)', zIndex: 0 }} />

              {stops.map((stop, idx) => {
                const sty = stopTypeStyle(stop.stop_type);
                return (
                  <div key={stop.id} style={{ display: 'flex', gap: '14px', marginBottom: '12px', position: 'relative', zIndex: 1 }}>
                    {/* Timeline dot */}
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: sty.bg, border: `2px solid ${sty.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                      {stop.stop_type === 'pickup' ? '📦' : stop.stop_type === 'delivery' ? '🏁' : stop.stop_type === 'port' ? '🚢' : '↕️'}
                    </div>

                    {/* Stop card */}
                    <div style={{ flex: 1, background: sty.bg, border: `1px solid ${sty.border}`, borderRadius: '14px', padding: '14px 16px', minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
                        <div>
                          <span style={{ fontFamily: 'Syne', fontWeight: '800', fontSize: '17px' }}>{stop.city}</span>
                          <span style={{ fontSize: '12px', color: 'var(--olive)', marginLeft: '8px' }}>{stop.country}</span>
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: '700', background: 'rgba(255,255,255,0.08)', borderRadius: '100px', padding: '2px 10px' }}>
                          {sty.label}
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px', color: 'rgba(245,237,216,0.7)' }}>
                        {stop.stop_date && (
                          <span>📅 {format(new Date(stop.stop_date), 'dd MMM yyyy')}
                            {stop.stop_date_until && stop.stop_date_until !== stop.stop_date
                              ? ` → ${format(new Date(stop.stop_date_until), 'dd MMM yyyy')}`
                              : ''}
                          </span>
                        )}
                        {stop.time_from && stop.time_until && (
                          <span>🕐 {stop.time_from.slice(0,5)} – {stop.time_until.slice(0,5)}</span>
                        )}
                        {stop.available_kg != null && stop.stop_type !== 'port' && (
                          <span>⚖️ {stop.available_kg} kg available</span>
                        )}
                      </div>

                      {stop.address && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(245,237,216,0.6)', display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                          <span>📍</span>
                          <span>{stop.address}</span>
                        </div>
                      )}
                      {stop.notes && (
                        <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--olive)', fontStyle: 'italic' }}>
                          💬 {stop.notes}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Simple trip route */}
        {!trip.is_multi_stop && trip.notes && (
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: '16px', marginBottom: '24px' }}>
            <div style={{ fontWeight: '700', marginBottom: '6px', fontSize: '13px', color: 'var(--olive)' }}>{t('itinerary.transporterNotes')}</div>
            <p style={{ fontSize: '14px', lineHeight: '1.6', margin: 0 }}>{trip.notes}</p>
          </div>
        )}

        {/* ── SHARE ROW ──────────────────────────────────────── */}
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px', marginBottom: '24px' }}>
          <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>{t('itinerary.shareTitle')}</div>
          <p style={{ fontSize: '13px', color: 'var(--olive)', marginBottom: '14px', lineHeight: '1.5' }}>
            {t('itinerary.shareSubtitle')}
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <a
              href={`https://wa.me/?text=${whatsappText}`}
              target="_blank" rel="noreferrer"
              className="btn btn-primary"
              style={{ background: '#25D366', fontSize: '14px', gap: '8px' }}
            >
              {t('itinerary.shareWhatsApp')}
            </a>
            <button onClick={copyLink} className="btn btn-ghost" style={{ fontSize: '14px' }}>
              {copied ? t('itinerary.copied') : t('itinerary.copyLink')}
            </button>
          </div>
        </div>

        {/* ── CTA ────────────────────────────────────────────── */}
        {trip.status === 'active' && trip.available_kg > 0 && (
          <Link to={`/trip/${id}`} className="btn btn-primary" style={{ width: '100%', padding: '15px', fontSize: '16px', fontWeight: '700' }}>
            {t('itinerary.bookSpot')}
          </Link>
        )}
        {trip.available_kg === 0 && (
          <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(255,255,255,0.04)', borderRadius: '14px', color: 'var(--olive)', fontSize: '14px' }}>
            {t('itinerary.fullyBooked')}
          </div>
        )}
      </div>
    </div>
  );
}